import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiThermometer,
  FiDroplet,
  FiWind,
  FiZap,
  FiWifi,
  FiWifiOff,
  FiPlus,
  FiMapPin,
  FiCpu,
  FiActivity,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiLink2,
} from "react-icons/fi";
import InstallPWAButton from "../components/InstallPWAButton";
import { useSocket } from "../context/SocketContext";
import { useAuth } from "../context/AuthContext";
import { getMCUsByClientId } from "../api/mcuApi";
import { getMyFarms } from "../api/farmApi";
import SensorTile from "../components/SensorTile";
import { toast } from "react-toastify";
import AcController from "../components/AcController";

const Dashboard = () => {
  const { clientData } = useAuth();
  const { sendCommand, realtimeData, connectedDevices, deviceStatusUpdate } =
    useSocket();

  const [farms, setFarms] = useState([]);
  const [mcudata, setMcudata] = useState([]);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [lastDataReceived, setLastDataReceived] = useState({});

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 30 * 1000); // every 30 seconds

    return () => clearInterval(interval); // cleanup on unmount
  }, []);

  // Add this effect to track when realtime data arrives
  useEffect(() => {
    Object.keys(realtimeData).forEach((mac) => {
      if (realtimeData[mac]) {
        setLastDataReceived((prev) => ({
          ...prev,
          [mac]: Date.now(),
        }));
      }
    });
  }, [realtimeData]);

  useEffect(() => {
    const loadMCUs = async () => {
      if (!clientData?.client_id) return;
      const result = await getMCUsByClientId(clientData.client_id);
      setMcudata(result);
    };

    const loadFarms = async () => {
      try {
        if (!clientData?.client_id) return;
        const result = await getMyFarms(clientData.client_id);
        const formatted = result.map((farm) => ({
          ...farm,
          createdAt: new Date(farm.createdAt).toLocaleDateString(),
          lastUpdated: new Date(farm.updatedAt).toLocaleTimeString(),
        }));
        setFarms(formatted);
      } catch (err) {
        toast.info(err.message);
      }
    };

    loadMCUs();
    loadFarms();
  }, [clientData]);

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      case "offline":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <FiCheckCircle className="w-4 h-4" />;
      case "warning":
        return <FiAlertTriangle className="w-4 h-4" />;
      case "offline":
        return <FiWifiOff className="w-4 h-4" />;
      default:
        return <FiActivity className="w-4 h-4" />;
    }
  };

  const StatusIndicator = ({
    status,
    label,
    auto,
    onToggle,
    session = false,
  }) => {
    return (
      <div
        className={`flex flex-col gap-2 p-3  border rounded-lg shadow-sm w-full ${
          session ? "bg-green-500" : "bg-gray-50"
        }`}
      >
        {/* Label + Status Light */}
        <div className="flex items-center text-sm font-semibold text-gray-800">
          <div
            className={`w-2 h-2 rounded-full mr-2 ${
              status ? "bg-green-500" : "bg-red-500"
            }`}
          />
          {label}
        </div>

        {/* Automation Switch */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600">Automation</span>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={auto}
              onChange={(e) =>
                onToggle &&
                onToggle({ type: "auto", onOff: e.target.checked, label })
              }
            />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 relative transition-all">
              <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
            </div>
          </label>
        </div>

        {/* Status Switch */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600">Status</span>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={status}
              onChange={(e) =>
                onToggle &&
                onToggle({ type: "status", onOff: e.target.checked, label })
              }
            />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-600 relative transition-all">
              <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
            </div>
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <InstallPWAButton />

      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiMapPin className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Farms</p>
              <p className="text-2xl font-bold text-gray-900">{farms.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <FiCpu className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active MCUs</p>
              <p className="text-2xl font-bold text-gray-900">
                {
                  mcudata.filter(
                    (mcu) => deviceStatusUpdate[mcu.mac]?.status === "online"
                  ).length
                }
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <FiAlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Warnings</p>
              <p className="text-2xl font-bold text-gray-900">
                {farms.filter((farm) => farm.status === "warning").length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <FiWifiOff className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Offline MCUs</p>
              <p className="text-2xl font-bold text-gray-900">
                {
                  mcudata.filter(
                    (mcu) => deviceStatusUpdate[mcu.mac]?.status !== "online"
                  ).length
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Farms Overview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Your Farms</h2>
            <Link
              to="/farms"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiPlus className="w-4 h-4 mr-2" />
              Add Farm
            </Link>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {farms.map((farm) => (
              <div
                key={farm.farm_id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{farm.name}</h3>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      farm.status
                    )}`}
                  >
                    {getStatusIcon(farm.status)}
                    <span className="ml-1 capitalize">{farm.status}</span>
                  </span>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <FiMapPin className="w-4 h-4 mr-2" />
                    {farm.location}
                  </div>
                  <div className="flex items-center">
                    <FiCpu className="w-4 h-4 mr-2" />
                    {farm.mcuCount || 0} MCUs
                  </div>
                  <div className="flex items-center">
                    <FiClock className="w-4 h-4 mr-2" />
                    Updated {farm.lastUpdated}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* MCU Readings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              MCU Readings
            </h2>
            <Link
              to="/mcu"
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              View All
            </Link>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {mcudata.map((mcu) => {
              const liveReadings = realtimeData[mcu.mac] || {}; //this is from the websocket hook
              // const liveStatus = deviceStatusUpdate[mcu.mac] || {}; // This also if device send once device status update when the device boot, and rarely to send offline if it sudden turn off, so we cannot rely fully on it
              const readings = {
                ...JSON.parse(mcu.readings),
                ...liveReadings,
              };

              // const now = new Date(currentTime);
              // const lastUpdated = readings.lastUpdated
              //   ? new Date(readings.lastUpdated)
              //   : null;
              // const minutesSinceUpdate = lastUpdated
              //   ? (now - lastUpdated) / 60000
              //   : Infinity;

              // // If realtime data just came in, we consider it online
              // const hasRealtimeData = !!realtimeData[mcu.mac];

              // With this simple logic:
              const lastSeen = lastDataReceived[mcu.mac];
              const isOnline =
                lastSeen && currentTime - lastSeen <= 2 * 60 * 1000;

              const sensorCounts =
                mcu.sensors?.reduce((acc, sensor) => {
                  acc[sensor.type] = (acc[sensor.type] || 0) + 1;
                  return acc;
                }, {}) || {};

              return (
                <div
                  key={mcu.mcu_id}
                  className="border border-gray-200 rounded-lg p-6"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {mcu.name}
                      </h3>
                      <p className="text-sm text-gray-600">{mcu.farm?.name}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {isOnline ? (
                        <div className="flex items-center text-green-600">
                          <FiWifi className="w-4 h-4 mr-1" />
                          <span className="text-sm">Online</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-red-600">
                          <FiWifiOff className="w-4 h-4 mr-1" />
                          <span className="text-sm">Offline</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="text-sm text-gray-600 flex items-center space-x-2">
                    <FiLink2 className="w-4 h-4 text-green-600" />
                    <span>
                      Linked to: {mcu?.profile_links[0]?.profile?.name}
                    </span>
                  </div>

                  {isOnline && (
                    <>
                      {/* Sensor Readings */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <SensorTile
                          label={`Temperature (${readings?.DHT?.count || 0} / ${
                            sensorCounts["DHT"] || 0
                          })`}
                          value={
                            readings?.DHT?.temperature !== undefined
                              ? readings.DHT.temperature.toFixed(1)
                              : "--"
                          }
                          unit="°C"
                          Icon={FiThermometer}
                          bg="bg-red-100"
                          color="text-red-600"
                        />
                        <SensorTile
                          label={`Humidity (${readings?.DHT?.count || 0} / ${
                            sensorCounts["DHT"] || 0
                          })`}
                          value={
                            readings?.DHT?.humidity !== undefined
                              ? readings.DHT.humidity.toFixed(1)
                              : "--"
                          }
                          unit="%"
                          Icon={FiDroplet}
                          bg="bg-blue-100"
                          color="text-blue-600"
                        />
                        <SensorTile
                          label={`Soil Temp (${
                            readings?.SOIL_TEMP?.count || 0
                          } / ${sensorCounts["SOIL_TEMP"] || 0})`}
                          value={
                            readings?.SOIL_TEMP?.soil_temperature !== undefined
                              ? readings.SOIL_TEMP.soil_temperature.toFixed(1)
                              : "--"
                          }
                          unit="°C"
                          Icon={FiThermometer}
                          bg="bg-orange-100"
                          color="text-orange-600"
                        />
                                                <SensorTile
                          label={`Soil Moist (${
                            readings?.SOIL_TEMP?.count || 0
                          } / ${sensorCounts["SOIL_MOIST"] || 0})`}
                          value={
                            readings?.SOIL_MOIST?.soil_moist !== undefined
                              ? readings.SOIL_MOIST.soil_moist.toFixed(1)
                              : "--"
                          }
                          unit="%"
                          Icon={FiThermometer}
                          bg="bg-orange-100"
                          color="text-orange-600"
                        />
                        <SensorTile
                          label={`CO2 (${readings?.CO2?.count || 0} / ${
                            sensorCounts["CO2"] || 0
                          })`}
                          value={
                            readings?.CO2?.co2 !== undefined
                              ? readings.CO2?.co2.toFixed(1)
                              : "--"
                          }
                          unit="ppm"
                          Icon={FiWind}
                          bg="bg-green-100"
                          color="text-green-600"
                        />
                      </div>

                      <div className="border-t pt-4">
                        <p className="text-xs text-gray-500 mb-2">
                          Device Status
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <StatusIndicator
                            status={readings.Irrigation?.status}
                            auto={readings.Irrigation?.auto}
                            label="Irrigation"
                            session={readings.Irrigation?.session}
                            onToggle={({ type, onOff, label }) => {
                              // Send Socket.IO command
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, {
                                type,
                                onOff,
                                target: label,
                              });
                            }}
                          />
                          {/* <StatusIndicator
                            status={readings?.AC?.status}
                            label="AC"
                            auto={readings?.AC?.auto}
                            onToggle={({ type, onOff, label }) => {
                              // Send Socket.IO command
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, {
                                type,
                                power: onOff,
                                target: label,
                                  "temp": 24,
  "mode": 1,
  "fan": 2
                              });
                            }}
                          /> */}
                          <AcController
                            temp={readings?.AC?.temp || 24}
                            mode={readings?.AC?.mode || 1}
                            fan={readings?.AC?.fan || 1}  
                            protocol={mcu.protocol || "UNKNOWN"}
                            status={readings?.AC?.status || false}
                            auto={readings?.AC?.auto || false}  
                            count={readings?.AC?.count || 1}
                            onTempChange={(newTemp) => {
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, { type: "status", target: "AC", temp: newTemp,mode: readings?.AC?.mode, fan: readings?.AC?.fan ,power: readings?.AC?.status});
                            }
                            }
                            onModeChange={(newMode) => {
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, { type: "status", target: "AC", mode: newMode,temp: readings?.AC?.temp, fan: readings?.AC?.fan ,power: readings?.AC?.status });
                            } }   
                            onFanChange={(newFan) => {
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, { type: "status", target: "AC", fan: newFan,temp: readings?.AC?.temp,  mode: readings?.AC?.mode ,power: readings?.AC?.status });
                            }
                          }
                            onPowerToggle={(newStatus) => {
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, { type: "status", target: "AC", power: newStatus, temp: readings?.AC?.temp, mode: readings?.AC?.mode, fan: readings?.AC?.fan});
                            } 
                          }
                            
                            onAutoToggle={(newAuto) => {
                              const deviceId = mcu.mac;   
                              sendCommand(deviceId, { type: "auto", target: "AC", onOff: newAuto });
                            }
                            }
                          />


                                                <StatusIndicator
                            status={readings?.Ventilation?.status}
                            label="Ventilation"
                            auto={readings?.Ventilation?.auto}
                            onToggle={({ type, onOff, label }) => {
                              // Send Socket.IO command
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, {
                                type,
                                onOff,
                                target: label,
                              });
                            }}
                          />
                          <StatusIndicator
                            status={readings?.Humidifier?.status}
                            label="Humidifier"
                            auto={readings?.Humidifier?.auto}
                            onToggle={({ type, onOff, label }) => {
                              // Send Socket.IO command
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, {
                                type,
                                onOff,
                                target: label,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Last Updated */}
                  <div className="mt-3 text-xs text-gray-500">
                    Last updated: {readings.lastUpdated || "No data"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
